import { SvgTune } from '@loomhq/lens';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  endThumbnailFlow,
  endVideoSettingsFlow,
  startVideoSettingsFlow,
} from 'actions/editor';
import { requestVideoPlayerLeaveFullScreen } from 'actions/videoPlayer';
import { toggleViewerInsightsUI } from 'actions/viewerInsights';
import { VIDEO_SETTINGS_ACCESS } from 'constants/scopes';
import { clearCTAWidgetState } from 'creators/editor';

import { selectIsActive } from 'selectors/viewerInsights';

import AddVideoCtaButton from '../add-video-cta-button';
import CollapsibleSideButton from '../collapsible-side-button';
import CustomThumbnailButton from '../custom-thumbnail-button';
import TranscriptOverlayButton from '../transcript-overlay-button';
import TrimModeButton from '../trim-mode-button';
import VideoButtons from '../video-buttons';

import Scopes from 'components/scopes';

export default ({
  // collapsed = only icons are showing, text is hidden
  // collapsible = can change state into collapsed state (false = force uncollapsed)
  // hidden = this component renders nothing
  isCollapsible = false,
  isHidden,
  onEnterTrimMode,
  startCollapsed = false,
  waitingForTrim = false,
}) => {
  const dispatch = useDispatch();
  const appState = useSelector(state => ({
    areThumbnailSettingsOpen: state.editor.thumbnail_flow,
    areVideoCtaSettingsOpen: state.editor?.cta_flow,
    areVideoSettingsOpen: state.editor?.video_settings_flow,
    areViewerInsightsOpen: selectIsActive(state),
    isVideoPlayerFullScreen: state.video_player?.full_screen,
  }));

  const {
    areThumbnailSettingsOpen,
    areVideoCtaSettingsOpen,
    areVideoSettingsOpen,
    areViewerInsightsOpen,
    isVideoPlayerFullScreen,
  } = appState;

  return (
    <VideoButtons
      startCollapsed={startCollapsed}
      isCollapsible={isCollapsible}
      isHidden={isHidden}
    >
      {/* VideoButtons uses the Function As Child Component (FaCC) pattern */}
      {({ isCollapsed }) => (
        <div className="p:xsmall">
          <TranscriptOverlayButton isCollapsed={isCollapsed} />
          <Scopes names={VIDEO_SETTINGS_ACCESS}>
            <CollapsibleSideButton
              onClick={() => {
                if (areViewerInsightsOpen) {
                  dispatch(toggleViewerInsightsUI());
                }

                if (areVideoCtaSettingsOpen) {
                  dispatch(clearCTAWidgetState());
                }

                if (areThumbnailSettingsOpen) {
                  dispatch(endThumbnailFlow());
                }

                // we only request leaving full screen if already full screen,
                // otherwise we queue up the request to leave full screen
                if (isVideoPlayerFullScreen) {
                  dispatch(requestVideoPlayerLeaveFullScreen());
                  dispatch(startVideoSettingsFlow());
                } else {
                  dispatch(
                    areVideoSettingsOpen
                      ? endVideoSettingsFlow()
                      : startVideoSettingsFlow()
                  );
                }
              }}
              icon={<SvgTune style={{ transform: 'rotate(90deg)' }} />}
              isCollapsed={isCollapsed}
            >
              Video preferences
            </CollapsibleSideButton>
          </Scopes>
          <TrimModeButton onClick={onEnterTrimMode} isCollapsed={isCollapsed} />
          {!waitingForTrim && (
            <>
              <AddVideoCtaButton isCollapsed={isCollapsed} />
              <CustomThumbnailButton isCollapsed={isCollapsed} />
            </>
          )}
        </div>
      )}
    </VideoButtons>
  );
};
